import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './ITSoftwarePage.css';
import { Link } from "react-router-dom";

const ITSoftwarePage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // const slideInRight = {
  //   hidden: { opacity: 0, x: 50 },
  //   visible: {
  //     opacity: 1,
  //     x: 0,
  //     transition: { duration: 0.8, ease: "easeOut" }
  //   }
  // };

  const keyFeatures = [
    {
      icon: "bi-code-slash",
      title: "Custom Software Development",
      description: "Streamline custom development projects with integrated project management, time tracking, and resource allocation.",
      color: "#0066CC"
    },
    {
      icon: "bi-cloud-arrow-up",
      title: "Cloud Services & SaaS Management",
      description: "Manage cloud infrastructure, subscription billing, and multi-tenant applications with automated provisioning.",
      color: "#1E40AF"
    },
    {
      icon: "bi-shield-check",
      title: "Cybersecurity & Compliance",
      description: "Ensure data security, compliance management, and risk assessment with integrated security protocols.",
      color: "#0066CC"
    },
    {
      icon: "bi-people-fill",
      title: "IT Support & Helpdesk",
      description: "Efficient ticketing system, SLA management, and customer support with automated workflows and escalation.",
      color: "#1E40AF"
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Digital Transformation Consulting",
      description: "Guide businesses through digital transformation with project tracking, milestone management, and ROI analysis.",
      color: "#0066CC"
    },
    {
      icon: "bi-laptop",
      title: "Enterprise Software Solutions",
      description: "Deploy enterprise-grade solutions with license management, user provisioning, and integration capabilities.",
      color: "#1E40AF"
    }
  ];

  const benefits = [
    {
      icon: "bi-speedometer2",
      title: "Software Development Companies",
      description: "Accelerate development cycles with integrated project management, resource tracking, and client billing.",
      stats: "40% faster delivery"
    },
    {
      icon: "bi-cloud",
      title: "SaaS & Cloud Service Providers",
      description: "Optimize subscription management, usage tracking, and automated billing for scalable SaaS operations.",
      stats: "60% billing efficiency"
    },
    {
      icon: "bi-shield-shaded",
      title: "Cybersecurity Firms",
      description: "Streamline security assessments, compliance reporting, and incident response management systems.",
      stats: "50% faster response"
    },
    {
      icon: "bi-headset",
      title: "IT Support & MSPs",
      description: "Enhanced ticketing systems, SLA monitoring, and automated client reporting for managed service providers.",
      stats: "35% productivity gain"
    },
    {
      icon: "bi-diagram-3",
      title: "Digital Transformation Consultants",
      description: "Track transformation projects, measure ROI, and manage client engagements with comprehensive analytics.",
      stats: "45% project success rate"
    },
    {
      icon: "bi-building",
      title: "Enterprise IT Departments",
      description: "Centralize IT operations, asset management, and vendor relationships for large-scale enterprise environments.",
      stats: "30% cost reduction"
    }
  ];

  const processSteps = [
    {
      title: "Enhanced Development Velocity",
      description: "Accelerate software development with integrated tools and automated workflows",
      icon: "bi-lightning"
    },
    {
      title: "Streamlined Client Management",
      description: "Optimize client relationships and project delivery with comprehensive CRM integration",
      icon: "bi-people"
    },
    {
      title: "Real-time Performance Analytics",
      description: "Monitor key metrics, project health, and business performance with live dashboards",
      icon: "bi-graph-up"
    },
    {
      title: "Improved Profitability",
      description: "Optimize resource allocation, billing accuracy, and cost management strategies",
      icon: "bi-currency-dollar"
    }
  ];

  return (
    <div className="it-software-page">
      <Helmet>
        <title>NetSuite for IT & Software Companies USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/industries/it-software" />
        <meta name="description" content="Optimize your IT and software business operations with SpireNest's NetSuite solutions, designed for the dynamic U.S. tech industry." />
        <meta name="keywords" content="NetSuite IT software, ERP for software companies, CRM for IT companies, USA, SpireNest" />
        <meta property="og:title" content="NetSuite for IT & Software Companies | SpireNest USA" />
        <meta property="og:description" content="Streamline IT operations with customized NetSuite ERP for software companies." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/industries/it-software" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-it.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite for IT & Software Companies | SpireNest USA" />
        <meta name="twitter:description" content="Empowering IT businesses with industry-focused NetSuite ERP solutions." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-it.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        {/* <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div>
         */}
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-code-slash me-2"></i>
                    IT & SOFTWARE INDUSTRY
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  NetSuite ERP for <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>IT & Software Industry</span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Transform your IT and software business with our comprehensive NetSuite ERP solution. 
                  Streamline project management, automate billing processes, and scale your operations 
                  effortlessly. From custom development to cloud services, manage every aspect of your 
                  technology business with precision, efficiency, and real-time visibility.
                </motion.p>
                
                {/* <div >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                    variant="light"
                    size="lg"
                    className="rounded-pill px-4 py-2 shadow fw-bold"
                    >
                       <a href="/contact" className="btn  btn-lg px-4">
   <i className="bi-rocket-takeoff me-2"></i>                   get started today 
                      </a>
                    
                    </Button>
                </motion.div>
                </div> */}

              </motion.div>
            </Col>
            
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: 45 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="hero-visual"
              >
                <div className="it-visual">
                  <motion.div 
                    className="code-container"
                    animate={{ rotateY: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-code-slash code-icon" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                  <motion.div 
                    className="cloud-small-1"
                    animate={{ y: [-10, 10] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-cloud-fill" style={{color: '#FF6B35'}}></i>
                  </motion.div>
                  <motion.div 
                    className="laptop-small-2"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-laptop" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              {/* <Badge className="section-badge mb-3">KEY FEATURES</Badge> */}
              <h2 className="section-title">Key Features of NetSuite</h2>
              <p className="section-description">
                Comprehensive IT and software capabilities designed for modern technology businesses
              </p>
            </motion.div>
            
            <Row className="g-4">
              {keyFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card className="feature-card h-100 border-0">
                      <Card.Body className="p-4">
                        <motion.div 
                          className="feature-icon mb-3"
                          style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <i className={`bi ${feature.icon}`}></i>
                        </motion.div>
                        <h5 className="feature-title mb-3">{feature.title}</h5>
                        <p className="feature-description">{feature.description}</p>
                        
                        <motion.div
                          className="feature-accent"
                          style={{ background: feature.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: "60px" }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title">Who Benefits from NetSuite for IT & Software</h2>
                  <p className="section-description">
                    Discover how different technology sectors leverage NetSuite to accelerate growth, 
                    improve efficiency, and achieve operational excellence in the digital landscape.
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="h-100"
                  >
                    <Card className="benefit-card h-100 border-0">
                      <Card.Body className="p-4 text-center">
                        <motion.div 
                          className="benefit-icon mb-3"
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                        >
                          <i className={`bi ${benefit.icon}`}></i>
                        </motion.div>
                        <h5 className="benefit-title mb-3">{benefit.title}</h5>
                        <p className="benefit-description mb-3">{benefit.description}</p>
                        <Badge className="stats-badge">{benefit.stats}</Badge>
                      </Card.Body>
                      
                      <motion.div
                        className="card-accent"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Benefits Section */}
      <section className="process-section py-5">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Transform Your IT & Software Operations</h2>
            </motion.div>
            
            <Row className="g-4">
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -15 }}
                    className="process-card text-center"
                  >
                    <motion.div 
                      className="process-icon mb-4"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <i className={`bi ${step.icon}`}></i>
                    </motion.div>
                    <h5 className="process-title mb-3">{step.title}</h5>
                    <p className="process-description">{step.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="cta-content">
              <motion.div
                className="cta-icon mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <i className="bi-rocket-takeoff"></i>
              </motion.div>
              <h2 className="cta-title mb-4">Ready to Transform Your IT Business?</h2>
              <p className="cta-description mb-4">
                Join thousands of IT companies who have revolutionized their operations with NetSuite ERP
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className="btn-primary-custom btn-lg px-5 py-3">
                    <i className="bi-chat-dots me-2"></i>
                    Get Started Today
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default ITSoftwarePage;